import { createOrUpdateAttendance } from './attendanceApi';
import { apiClient } from '.';

// 강의 API 관련 타입 정의
export interface Lecture {
  id: number;
  title: string;
  caption: string;
  thumbnail_url: string | null;
  lecturer_info?: {
    id: number;
    name: string;
    introduction: string;
    image_url: string | null;
  } | null;
}

export interface Chapter {
  uri: string;
  title: string;
  timecode: number;
  thumbnails: any[];
  chapterNumber?: number; // 챕터 번호 추가
}

export interface LectureListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    content: Lecture[];
    pageable: {
      pageNumber: number;
      pageSize: number;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      offset: number;
      paged: boolean;
      unpaged: boolean;
    };
    totalElements: number;
    totalPages: number;
    last: boolean;
    size: number;
    number: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    numberOfElements: number;
    first: boolean;
    empty: boolean;
  };
}

export interface LectureListParams {
  page?: number;
  size?: number;
}

// 시간 포맷팅 유틸리티 함수
export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
};

// 강의 목록 조회 API
export const fetchLectures = async (params: LectureListParams = {}): Promise<LectureListResponse> => {
  const { page = 0, size = 3 } = params;
  
  const url = `/api/lectures/info/list?page=${page}&size=${size}`;

  try {
    /// console.log(`강의 목록 조회 API 요청 데이터`, {page, size, url});
    const { data } = await apiClient.get<LectureListResponse>(url, { withCredentials: true });
    /// console.log(`강의 목록 조회 API 응답 데이터`, {data});
    
    return data;
  } catch (error) {
    /// console.error(`[lectureApi] 강의 목록 조회 실패:`, error);
    throw error;
  }
};

export interface ChapterListResponse {
  code: number;
  status: string;
  message: string;
  data: Chapter[];
}

// 강의 챕터 조회 API
export const getLectureChapters = async (lectureId: number): Promise<ChapterListResponse> => {
  const url = `/api/lectures/info/${lectureId}/chapters`;

  try {
    /// console.log('강의 챕터 조회 API id:', lectureId);
    
    const { data } = await apiClient.get<ChapterListResponse>(
      url,
      { withCredentials: true }
    );
    /// console.log('강의 챕터 조회 API 응답:', data);
    
    return data;
  } catch (error) {
    /// console.error('강의 챕터 조회 실패:', error);
    throw error;
  }
};

// 강의 시청 기록 및 출석 처리 API
export const recordLectureWatch = async (lectureId: number, watchedDuration: number): Promise<{ success: boolean }> => {
  try {
    // 1초 이상 시청했으면 출석 처리
    if (watchedDuration >= 1) {
      const attendanceResult = await createOrUpdateAttendance({
        lectureId,
        watchedDuration
      });
      
      if (attendanceResult.success) {
        /// console.log(`강의 ${lectureId} 시청으로 출석 처리됨 (${watchedDuration}초)`);
      }
      
      return attendanceResult;
    }
    
    return { success: true }; // 1초 미만은 출석 처리 안함
  } catch (error) {
    /// console.error('강의 시청 기록 실패:', error);
    return { success: false };
  }
};

// 초(seconds)를 h-m-s 형식으로 변환하는 함수
export const formatProgressTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  return `${hours}h${minutes}m${remainingSeconds}s`;
};

// h-m-s 형식을 초(seconds)로 변환하는 함수
export const parseProgressTime = (progressTime: string): number => {
  const regex = /(\d+)h(\d+)m(\d+)s/;
  const match = progressTime.match(regex);
  
  if (!match) return 0;
  
  const hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const seconds = parseInt(match[3]);
  
  return hours * 3600 + minutes * 60 + seconds;
};

// Q&A 게시글 타입 정의
export interface QnaPost {
  post_id: number;
  title: string;
  content: string;
  post_category: string;
  reply_cnt: number;
  view_cnt: number;
  writer_name: string;
  created_at: string;
  lecture_name?: string;
  chapter?: number;
  study_name?: string;
  pinned: boolean;
}

// 게시글 상세 정보 타입 정의
export interface PostDetail {
  post_category: string;
  post_title: string;
  post_content: string;
  writer_name: string;
  created_at: string;
  can_update: boolean;
  reply_cnt: number;
  view_cnt: number;
  lecture_title?: string;
  lecture_chapter?: string;
  study_name?: string;
  replies: Reply[];
}

export interface Reply {
  reply_id: number;
  content: string;
  writer_name: string;
  reply_created_at: string;
  images?: string[];
}

export interface PostDetailResponse {
  code: number;
  status: string;
  message: string;
  data: PostDetail;
}

export interface QnaListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: QnaPost[];
    number: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    numberOfElements: number;
    pageable: {
      offset: number;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      paged: boolean;
      pageNumber: number;
      pageSize: number;
      unpaged: boolean;
    };
    first: boolean;
    last: boolean;
    empty: boolean;
  };
}

// 강의별 Q&A 목록 조회 API
export const getLectureQnaPosts = async (
  lectureId: number, 
  page: number = 0, 
  size: number = 10,
  chapter?: number
): Promise<QnaListResponse> => {
  
  // 쿼리 파라미터 구성
  const params = new URLSearchParams({
    board: 'lecture',
    lectureId: lectureId.toString(),
    page: page.toString(),
    size: size.toString()
  });
  
  // 챕터가 지정된 경우 추가
  if (chapter !== undefined) {
    params.append('chapter', chapter.toString());
  }
  
  const url = `/api/posts/list?${params.toString()}`;

  try {
    /// console.log('Q&A 목록 조회:', { lectureId, page, size, chapter, url });
    
    const { data } = await apiClient.get<QnaListResponse>(url, { withCredentials: true });
    /// console.log('QnA API response:', data);
    
    return data;
  } catch (error) {
    /// console.error('Q&A 목록 조회 실패:', error);
    throw error;
  }
};

// 게시글 상세조회 API
export const getPostDetail = async (postId: number): Promise<PostDetailResponse> => {
  const url = `/api/posts/${postId}`;

  try {
    /// console.log('게시글 상세조회 postid:', postId);
    
    const { data } = await apiClient.get<PostDetailResponse>(url, { withCredentials: true });
    /// console.log('게시글 상세조회 API 응답:', {data});
    
    return data;
  } catch (error) {
    /// console.error('게시글 상세조회 실패:', error);
    throw error;
  }
};

// 강의 수강 진행률 조회 API
export const getLectureProgress = async (lectureId: number) => {
  const url = `/api/me/progress?lectureId=${lectureId}`;

  try {
    const { data } = await apiClient.get(url, { withCredentials: true });
    
    console.log('[진행률 조회 응답] 백엔드 응답:', JSON.stringify(data, null, 2));
    
    // 응답에 completion_rate가 있으면 로그 출력
    if (data?.data?.completion_rate !== undefined) {
      const rate = data.data.completion_rate;
      const completionRatePercent = rate < 1 ? rate * 100 : rate;
      // console.log(`[진행률 조회 응답] 백엔드에서 반환한 completion_rate: ${completionRatePercent}%`);
    }
    
    if (data?.data?.total_time_watched) {
      // console.log(`[진행률 조회 응답] 백엔드에서 반환한 total_time_watched: ${data.data.total_time_watched}`);
    }
    
    return data;
  } catch (error) {
    console.error('[진행률 조회 실패]', error);
    throw error;
  }
};

// 강의 수강 진행률 업데이트 API
export const updateLectureProgress = async (lectureId: number, progressSeconds: number) => {
  const url = `/api/enrollments/${lectureId}/progress`;
  
  const progressTime = formatProgressTime(progressSeconds);
  const requestBody = { progress: progressTime };

  try {
    console.log('[진행률 업데이트 요청]:', {requestBody, url});
    
    const { data } = await apiClient.put(
      url,
      requestBody,
      { withCredentials: true }
    );
    
    console.log('[진행률 업데이트 응답] 백엔드 응답:', JSON.stringify(data, null, 2));
    
    // 응답에 completion_rate가 있으면 로그 출력
    if (data?.data?.completion_rate !== undefined) {
      const rate = data.data.completion_rate;
      const completionRatePercent = rate < 1 ? rate * 100 : rate;
      // console.log(`[진행률 업데이트 응답] 백엔드에서 반환한 completion_rate: ${completionRatePercent}%`);
    }
    
    // 응답에 total_time_watched가 있으면 로그 출력
    if (data?.data?.total_time_watched) {
      // console.log(`[진행률 업데이트 응답] 백엔드에서 반환한 total_time_watched: ${data.data.total_time_watched}`);
    }
    
    return data;
  } catch (error) {
    console.error('[진행률 업데이트 실패]', error);
    throw error;
  }
};

// 설문 카테고리 조회 타입
export interface SurveyCategory {
  id: number;
  name: string;
  is_active: boolean;
}

export interface SurveyCategoriesResponse {
  code: number;
  status: string;
  message: string;
  data: SurveyCategory[];
}

// 설문 질문 조회 타입
export interface SurveyQuestion {
  id: number;
  question: string;
  category_id: number;
  category_name: string;
  selection_count: number;
  active: boolean;
}

export interface SurveyQuestionsResponse {
  code: number;
  status: string;
  message: string;
  data: SurveyQuestion[];
}

// 설문 응답 제출 타입
export interface SurveyAnswer {
  surveyId: number;
  answer: string;
}

export interface SurveySubmitRequest {
  lectureId: number;
  answers: SurveyAnswer[];
}

export interface SurveySubmitResponse {
  code: number;
  status: string;
  message: string;
  data: {
    lecture_id: number;
    responser_name: string;
    responser_id: number;
    results: Array<{
      survey_result_id: number;
      survey_id: number;
      question: string;
      answer: string;
    }>;
  };
}

// 설문 카테고리 목록 조회
export const getSurveyCategories = async (): Promise<SurveyCategoriesResponse> => {
  try {
    /// console.log('설문 카테고리 목록 조회 API 요청');
    const { data } = await apiClient.get<SurveyCategoriesResponse>('/api/lecture-survey/categories/list');
    /// console.log('설문 카테고리 목록 조회 API 응답:', data);
    return data;
  } catch (error) {
    /// console.error('설문 카테고리 목록 조회 실패:', error);
    throw error;
  }
};

// 설문 질문 목록 조회
export const getSurveyQuestions = async (): Promise<SurveyQuestionsResponse> => {
  try {
    /// console.log('설문 질문 목록 조회 API 요청');
    const { data } = await apiClient.get<SurveyQuestionsResponse>('/api/lecture-survey/list');
    /// console.log('설문 질문 목록 조회 API 응답:', data);
    return data;
  } catch (error) {
    /// console.error('설문 질문 목록 조회 실패:', error);
    throw error;
  }
};

// 모든 설문 질문 조회 (새로운 API 사용)
export const getAllSurveyQuestions = async (): Promise<SurveyQuestion[]> => {
  try {
    const response = await getSurveyQuestions();
    if (response.code === 200 || response.code === 1073741824) {
      // active가 true인 질문만 필터링
      return response.data.filter(question => question.active);
    } else {
      throw new Error(response.message || '설문 질문을 불러오는데 실패했습니다.');
    }
  } catch (error) {
    /// console.error('모든 설문 질문 조회 실패:', error);
    throw error;
  }
};

// 강의 설문 제출 (새로운 API 사용)
export const submitLectureSurvey = async (lectureId: number, responses: { [key: string]: number | string }): Promise<SurveySubmitResponse> => {
  try {
    const answers: SurveyAnswer[] = Object.entries(responses).map(([key, value]) => {
      const surveyId = parseInt(key.replace('question_', ''));
      return {
        surveyId: surveyId,
        answer: value.toString()
      };
    });

    const requestData: SurveySubmitRequest = {
      lectureId: parseInt(lectureId.toString()),
      answers: answers
    };

    /// console.log('설문 제출 요청:', requestData);

    const { data } = await apiClient.post<SurveySubmitResponse>('/api/lecture-survey/answers', requestData);
    /// console.log('설문 제출 응답:', data);
    return data;
  } catch (error: any) {
    /// console.error('설문 제출 실패:', error);
    // 서버 에러 메시지를 포함하여 에러 재발생
    if (error.response?.data?.message) {
      const serverError = new Error(error.response.data.message);
      (serverError as any).response = error.response;
      throw serverError;
    }
    throw error;
  }
}; 
